import React from 'react';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

/**
 * Primitivos de UI de dashboard — promovidos de `modules/dashboards-commercial`
 * para shared em 2026-08-21 (réplica EXATA; memória ds_reference_exact_parity).
 * Consumidores: Dashboard Comercial, Dashboard do vendedor (CRM), Ranking.
 * Tokens SOMENTE via var(--theme-*) — inclusive cores de status
 * (success/error via *-subtle / *-fg, sem hex hardcoded — F63).
 */

// ---------- formatters ----------
export const formatBRL = (v: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(v);

export const formatBRLShort = (v: number): string => {
  if (v === 0) return 'R$ 0';
  if (v >= 1000) return `R$ ${(v / 1000).toFixed(1).replace('.', ',')}k`;
  return `R$ ${v}`;
};

export const formatNumber = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(v);

export const formatPct = (v: number): string =>
  `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(v)}%`;

export const formatDeltaPct = (v: number): string =>
  `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(Math.abs(v))}%`;

// ---------- SectionLabel ----------
export const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="text-[11px] font-bold uppercase tracking-wider px-0.5"
    style={{ color: 'var(--theme-muted-text)' }}
  >
    {children}
  </div>
);
SectionLabel.displayName = 'SectionLabel';

// ---------- DeltaBadge ----------
type DeltaTrend = 'up' | 'down' | 'neutral';

interface DeltaBadgeProps {
  deltaPct: number;
  /** Se omitido, deriva do sinal de deltaPct. */
  trend?: DeltaTrend;
  className?: string;
}

export const DeltaBadge: React.FC<DeltaBadgeProps> = ({ deltaPct, trend, className }) => {
  const resolved: DeltaTrend = trend ?? (deltaPct > 0 ? 'up' : deltaPct < 0 ? 'down' : 'neutral');
  const positive = resolved === 'up';
  const negative = resolved === 'down';
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${className ?? ''}`}
      style={{
        backgroundColor: positive
          ? 'var(--theme-success-subtle)'
          : negative
            ? 'var(--theme-error-subtle)'
            : 'var(--theme-surface-sunken)',
        color: positive
          ? 'var(--theme-success-fg)'
          : negative
            ? 'var(--theme-error-fg)'
            : 'var(--theme-muted-text)',
      }}
    >
      {positive ? (
        <Icon name="trending-up" size={12} />
      ) : negative ? (
        <Icon name="trending-down" size={12} />
      ) : null}
      {formatDeltaPct(deltaPct)}
    </span>
  );
};
DeltaBadge.displayName = 'DeltaBadge';

// ---------- DashCard ----------
interface DashCardProps {
  icon: IconName;
  title: string;
  subtitle?: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
  className?: string;
  bodyClassName?: string;
  /** Remove o padding padrão do corpo (para tabelas/listas que gerenciam o próprio). */
  noPadding?: boolean;
}

export const DashCard: React.FC<DashCardProps> = ({
  icon,
  title,
  subtitle,
  headerRight,
  children,
  testId,
  className,
  bodyClassName,
  noPadding,
}) => (
  <div
    className={`rounded-xl overflow-hidden flex flex-col ${className ?? ''}`}
    style={{
      backgroundColor: 'var(--theme-card-background)',
      border: '1px solid var(--theme-card-border)',
    }}
    data-testid={testId}
  >
    <div
      className="flex items-center gap-3 px-4 py-3"
      style={{ borderBottom: '1px solid var(--theme-card-border)' }}
    >
      <span
        className="w-[34px] h-[34px] rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
      >
        <Icon name={icon} size={17} />
      </span>
      <div className="flex-1 min-w-0">
        <div
          className="text-base font-bold leading-tight truncate"
          style={{ color: 'var(--theme-card-text)' }}
        >
          {title}
        </div>
        {subtitle && (
          <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {subtitle}
          </div>
        )}
      </div>
      {headerRight && <div className="flex-shrink-0">{headerRight}</div>}
    </div>
    <div className={`flex-1 ${noPadding ? '' : 'p-4'} ${bodyClassName ?? ''}`}>{children}</div>
  </div>
);
DashCard.displayName = 'DashCard';

// ---------- MetricTile (KPI) ----------
interface MetricTileProps {
  icon: IconName;
  label: string;
  value: string;
  /** Variação contra o período anterior. Omitido = tela sem comparação: sem selo. */
  deltaPct?: number;
  trend?: DeltaTrend;
  caption?: string;
  /** Linha extra opcional (ex: no-show), em cor de erro. */
  extra?: string;
  testId: string;
}

export const MetricTile: React.FC<MetricTileProps> = ({
  icon,
  label,
  value,
  deltaPct,
  trend,
  caption,
  extra,
  testId,
}) => (
  <div
    className="rounded-xl px-4 py-4 flex flex-col gap-2 min-w-0"
    style={{
      backgroundColor: 'var(--theme-card-background)',
      border: '1px solid var(--theme-card-border)',
    }}
    data-testid={testId}
  >
    {/* Topo: rótulo à esquerda, chip de ícone à direita — mesma anatomia do
        MetricCard da dash de Atendimento. `min-w-0` no rótulo é o que permite o
        `truncate` funcionar sem empurrar o chip para fora. */}
    <div className="flex items-center justify-between gap-2 min-w-0">
      <span
        className="text-xs font-semibold truncate min-w-0"
        style={{ color: 'var(--theme-muted-text)' }}
      >
        {label}
      </span>
      <span
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
      >
        <Icon name={icon} size={16} />
      </span>
    </div>
    <div
      className="text-[26px] font-bold leading-none"
      style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </div>
    <div className="flex items-center gap-2 min-w-0">
      {deltaPct !== undefined && <DeltaBadge deltaPct={deltaPct} {...(trend ? { trend } : {})} />}
      {caption && (
        <span className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
          {caption}
        </span>
      )}
    </div>
    {extra && (
      <div className="text-xs font-semibold" style={{ color: 'var(--theme-error-fg)' }}>
        {extra}
      </div>
    )}
  </div>
);
MetricTile.displayName = 'MetricTile';

// ---------- ChartLegendChip ----------
interface ChartLegendChipProps {
  color: string;
  label: string;
  total: number;
  /** Texto depois do total (ex: a conversão dos leads). */
  extra?: string;
  testId: string;
}

/**
 * Chip de legenda de gráfico com várias séries: cor, nome e total do período.
 * Promovido do card de visitas do Analytics (2026-10-14) para o gráfico de
 * vendas do Comercial usar o mesmo.
 */
export const ChartLegendChip: React.FC<ChartLegendChipProps> = ({ color, label, total, extra, testId }) => (
  <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap" data-testid={testId}>
    <span className="w-2.5 h-2.5 rounded-sm flex-none" style={{ backgroundColor: color }} />
    <span style={{ color: 'var(--theme-muted-text)' }}>{label}</span>
    <span className="font-semibold" style={{ color: 'var(--theme-card-text)' }}>
      {formatNumber(total)}
    </span>
    {extra && <span style={{ color: 'var(--theme-muted-text)' }}>· {extra}</span>}
  </span>
);
ChartLegendChip.displayName = 'ChartLegendChip';
